import { fromEvent, map } from "rxjs";

export default class NameInput {
  constructor() {
    this.valueInput = '';
    this.errorMessageId = 'nhs_ErrorForName';
    this.id = 'Name';
    this.errorCssClass = 'error';
    this.labelClassName = 'nhs-u-text--visually-hidden';
    this.fieldClassName = 'form__input';
    this.errorClassName = 'form__error';
    this.labelText = 'Full Name';
    this.pattern = `^([A-Za-zà-ü]+(-?'?[A-Za-z+'?-?à-ü]+))(\\s([A-Za-zà-ü]+(-?'?[A-Za-z+'?-?à-ü]+)?))+?$`;
    this.matchingPattern = new RegExp(this.pattern);
  }

  set value(newValue){
    this.valueInput = newValue || '';
  }

  get value(){
    return this.valueInput;
  }

  /**
   * Overrides the CSS classes used by getHtml(). Defaults (set in the constructor) match every
   * existing consumer of this component; only screens that want different styling need to call
   * this — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {Object} classNames
   * @param {string} [classNames.labelClassName]
   * @param {string} [classNames.fieldClassName]
   * @param {string} [classNames.errorClassName]
   */
  setClassNames({ labelClassName, fieldClassName, errorClassName } = {}) {
    if (labelClassName !== undefined) {this.labelClassName = labelClassName;}
    if (fieldClassName !== undefined) {this.fieldClassName = fieldClassName;}
    if (errorClassName !== undefined) {this.errorClassName = errorClassName;}
    return this;
  }

  /**
   * Overrides the visible label text. Default (set in the constructor) matches every existing
   * consumer of this component; only screens that want different copy need to call this — see
   * GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} text
   */
  setLabelText(text) {
    this.labelText = text;
    return this;
  }

  /**
   * Overrides the input element's id used by getHtml()/getNode(). Default (set in the constructor)
   * matches every existing consumer of this component; a screen that can render alongside another
   * instance of this component on the same page (e.g. a persistent sidebar form) must override this
   * to avoid a duplicate-id collision — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} id
   */
  setInputId(id) {
    this.id = id;
    return this;
  }

  /**
   * Overrides the error-message element's id used by getHtml(). Default (set in the constructor)
   * matches every existing consumer of this component; a screen that can render alongside another
   * instance of this component on the same page (e.g. a persistent sidebar form) must override this
   * to avoid a duplicate-id collision — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} errorMessageId
   */
  setErrorMessageId(errorMessageId) {
    this.errorMessageId = errorMessageId;
    return this;
  }

  getHtml() {
    return `<label for="${this.id}" class="${this.labelClassName}">${this.labelText}</label><input data-qa="name-input" data-segment-identity-data="Name" id="${this.id}" class="${this.fieldClassName}"
            placeholder="*First and Last Name" maxlength="100" name="name" autocomplete="name"
            type="text" required value="${this.value}" data-form-name-input></input><span id="${this.errorMessageId}" class="${this.errorClassName}" hidden>
                           Please fill in your first and last name
                         </span>`;
  }

  getNode() {
    return document.getElementById(this.id);
  }

  /**
   * getOnChangeEvent: On input change map its value to the storage property.
   */
  getOnChangeEvent() {
    return fromEvent(this.getNode(), 'change').pipe(
      map((event) => ({name: event.target.value}))
    );
  }

  setInvalidState(node) {
    const errorMessage = document.getElementById(this.errorMessageId);

    node.setAttribute('aria-invalid', true);
    node.classList.add(this.errorCssClass);
    errorMessage.removeAttribute('hidden');
  }

  setValidState(node) {
    const errorMessage = document.getElementById(this.errorMessageId);

    node.removeAttribute('aria-invalid');
    node.classList.remove(this.errorCssClass);
    errorMessage.setAttribute('hidden', true);
  }

  focus() {
    const nameInput = this.getNode();
    nameInput.focus();
  }

  validate() {
    const node = this.getNode();
    const cleanedValue = node.value.trim().replace(/\s+/g, ' ');
    node.value = cleanedValue;

    const isValid = this.matchingPattern.test(cleanedValue);

    return new Promise((resolve) => {
      if (isValid) {
        this.setValidState(node);
        resolve(true);
      } else {
        this.setInvalidState(node);
        resolve(false);
      }
    });
  }
}
